import React, { useEffect, useContext, useMemo, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import _ from 'lodash';
import i18next from 'i18next';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { Accordion, AccordionDetails, AccordionSummary, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Box, Grid, Tab, Tabs, MenuItem, Select, ListSubheader } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import DifferenceIcon from '@mui/icons-material/Difference';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Control, useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { FormInputCurrency } from 'components/form/FormInputCurrency';
import { FormLabel } from 'components/form/FormLabel';
import { hintValidationAlert } from 'helper/form';
import { ContentBlockFormType, FormFieldsFormType, LineItemFormType, OfferVersionFormType, QuickPriceCalculatorContext, SeminarRoomRequest } from './context';
import SeminarRoomOfferFields from './SeminarRoomOfferFields';
import { isSeminarRoomFacility } from 'semshared/pricelist/seminarRoom';
import { FormInputRichText } from 'components/form/FormInputRichTextEditor';
import { formatOfferDay, localizeStoredSeminarDetails, splitIndividualSeatingDetails } from 'semshared/utils/format';
import { formatDate } from 'components/DateTime';

function localizedOfferDay(day: number): string {
  return formatOfferDay(day, (key, opts) => {
    if (key === 'common:checkout.pre_day') return String(i18next.t('offer-input-header-preday'));
    if (key === 'common:day_number') return `${i18next.t('offer-input-header-day')} ${opts?.number ?? ''}`;
    return '';
  });
}
import { formatPercentage } from 'components/Percentage';
import { NO_PRICE } from 'semshared/pricelist/quickprice';
import { LabeledFrame } from 'components/LabeledFrame';
import { ITaxRate } from 'semshared/pricelist/taxselector';
import { EOfferVersionContentBlockLineItemSource } from '__generated__/graphql';
import { quickPriceSkuDisplayName } from 'helper/quickPriceSkuDisplayName';

interface IEditorAccordionProps {
  cssStyles: string;
  contentBlock: ContentBlockFormType;
  contentBlockIndex: number;
  showHtmlTab: boolean;
  fullWidth: boolean;
  control: Control<OfferVersionFormType>;
  disabled: boolean;
  canChangeCounts: boolean;
  canChangeItems: boolean;
  canChangePrices: boolean;
  canDeleteItems: boolean;
  canAddItems: boolean;
  canChangeStructure: boolean;
  isPricesNet: boolean;
  /** Offer price list — used to prefer priced hierarchy products in the addon menu (SM-2581). */
  priceListId?: number;
  guestCount?: number;
  isPremium?: boolean;
  publicCompanyId?: number;
  canChangeLineItemCount?: (index: number, lineItemIndex: number) => boolean;
  canChangeLineItemPrice?: (index: number, lineItemIndex: number) => boolean;
  canChangeLineItemHeader?: (index: number, lineItemIndex: number) => boolean;
  canDeleteLineItem?: (index: number, lineItemIndex: number) => boolean;
  showLineItem?: (index: number, lineItemIndex: number) => boolean;
  isLineItemHighlighted?: (index: number, lineItemIndex: number) => boolean;
  onLineItemChanged: (index: number, lineItemIndex: number) => void;
  onLineItemCopyPrices: (index: number, lineItemIndex: number) => void;
  onLineItemPriceComponentChanged: (index: number, lineItemIndex: number, componentIndex: number) => void;
  onLineItemPriceItemManuallyChanged?: (index: number, lineItemIndex: number, newPriceItem: number) => void;
  onLineItemSKUAdded: (cbIndex: number, sku: string) => LineItemFormType | null | undefined;
  onLineItemFreeAdded: (cbIndex: number, taxTypeId: number | number[]) => LineItemFormType | null | undefined;
  onTotalsChanged: () => void;
  onDelete: (index: number) => void;
  hotelMessage?: string | null;
  spaceId?: number | null;
  roomRequests?: { day: number; occupancy?: SeminarRoomRequest[] | null }[] | null;
}

interface IEditorAccordionLineItemProps {
  contentBlock: ContentBlockFormType;
  contentBlockIndex: number;
  fullWidth: boolean;
  lineItemIndex: number;
  control: Control<OfferVersionFormType>;
  disabled: boolean;
  canChangeCounts: boolean;
  canChangeItems: boolean;
  canChangePrices: boolean;
  canDeleteItems: boolean;
  canAddItems: boolean;
  canChangeStructure: boolean;
  isPricesNet: boolean;
  canChangeLineItemCount?: (index: number, lineItemIndex: number) => boolean;
  canChangeLineItemPrice?: (index: number, lineItemIndex: number) => boolean;
  canChangeLineItemHeader?: (index: number, lineItemIndex: number) => boolean;
  canDeleteLineItem?: (index: number, lineItemIndex: number) => boolean;
  onLineItemRemoved: (index: number, lineItemIndex: number) => void;
  onLineItemChanged: (index: number, lineItemIndex: number) => void;
  onLineItemCopyPrices: (index: number, lineItemIndex: number) => void;
  onLineItemPriceComponentChanged: (index: number, lineItemIndex: number, componentIndex: number) => void;
  onLineItemPriceItemManuallyChanged?: (index: number, lineItemIndex: number, newPriceItem: number) => void;
  roomRequests?: { day: number; occupancy?: SeminarRoomRequest[] | null }[] | null;
}

interface IEditorAccordionFormFieldProps {
  cssStyles: string;
  contentBlock: ContentBlockFormType;
  contentBlockIndex: number;
  formFieldIndex: number;
  control: any;
  disabled?: boolean;
  spaceId?: number | null;
}

interface IEditorAccordionLineItemComponentProps {
  contentBlock: ContentBlockFormType;
  contentBlockIndex: number;
  lineItemIndex: number;
  componentIndex: number;
  control: any;
  disabled: boolean;
  isPricesNet: boolean;
  canChangeLineItemPrice?: (index: number, lineItemIndex: number) => boolean;
  onLineItemChanged: (index: number, lineItemIndex: number) => void;
  onLineItemPriceComponentChanged: (index: number, lineItemIndex: number, componentIndex: number) => void;
}

function _taxRateNameLabelComponent(
  isPricesNet: boolean,
  component: { name: string; taxTypeId: number },
  quickPriceCalculator: { getTaxRateForType: (taxTypeId: number) => ITaxRate | null } | undefined | null,
) {
  const taxRate = quickPriceCalculator && quickPriceCalculator.getTaxRateForType(component.taxTypeId);
  return taxRate ? taxRate.name : component.name;
}
function _taxRateNameLabelItem(
  isPricesNet: boolean,
  component: { name: string; taxTypeId: number } | null | undefined,
  quickPriceCalculator: { getTaxRateForType: (taxTypeId: number) => ITaxRate | null } | undefined | null,
) {
  const taxRate = quickPriceCalculator && component && quickPriceCalculator.getTaxRateForType(component.taxTypeId);
  return `${isPricesNet ? i18next.t('offerblock-lineitem-priceitem-net') : i18next.t('offerblock-lineitem-priceitem-gross')}${taxRate ? ` ${formatPercentage(taxRate.rate)}` : ''}`;
}

const DEBOUNCE_DELAY_MS = 500;

const facilityIsSeminarRoom = (calculator: { allFacilitiesDb?: { sku?: string | null; lookupCode?: string | null; meetingRoom?: unknown }[] | null } | null, sku?: string | null) => {
  const facility = calculator?.allFacilitiesDb?.find(item => item.sku === sku);
  return isSeminarRoomFacility(facility);
};

/** Shows the confirmation panel for a seminar-room line. A newly added room is only present in the form values. */
const SeminarRoomLine = ({
  namePrefix,
  control,
  disabled,
  contentBlock,
  lineItemIndex,
  roomRequests,
  quickPriceCalculator,
  header,
}: {
  namePrefix: `contentBlocks.${number}.lineItems.${number}`;
  control: Control<OfferVersionFormType>;
  disabled: boolean;
  contentBlock: ContentBlockFormType;
  lineItemIndex: number;
  roomRequests?: { day: number; occupancy?: SeminarRoomRequest[] | null }[] | null;
  quickPriceCalculator: { allFacilitiesDb?: { sku?: string | null; lookupCode?: string | null; meetingRoom?: unknown }[] | null } | null;
  header: string;
}) => {
  const sku = useWatch({ control, name: `${namePrefix}.sku` });
  const watchedHeader = useWatch({ control, name: `${namePrefix}.header` });
  const day = useWatch({ control, name: `${namePrefix}.day` }) ?? contentBlock.day ?? 0;
  const lineItemsPath = namePrefix.replace(/\.\d+$/, '') as `contentBlocks.${number}.lineItems`;
  const lineItems = useWatch({ control, name: lineItemsPath }) || [];
  let seminarIndex = 0;
  for (let index = 0; index < lineItemIndex; index += 1) {
    if (facilityIsSeminarRoom(quickPriceCalculator, lineItems[index]?.sku)) seminarIndex += 1;
  }
  const request = roomRequests?.find(entry => entry.day === day)?.occupancy?.[seminarIndex];

  if (!facilityIsSeminarRoom(quickPriceCalculator, sku)) return null;

  return (
    <Grid item xs={12}>
      <SeminarRoomOfferFields namePrefix={namePrefix} control={control} disabled={disabled} request={request} header={header || watchedHeader || ''} />
    </Grid>
  );
};

const IndividualSeatingHint = ({ text }: { text: string }) => (
  <Box
    component="span"
    onClick={event => event.stopPropagation()}
    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 1 }}
  >
    <Box component="span" sx={{ display: 'inline-grid', gridTemplateColumns: '6px 6px', gap: '2px', mr: 0.5 }}>
      {[0, 1, 2, 3].map(cell => (
        <Box key={cell} component="span" sx={{ width: 6, height: 6, border: '1px solid #082B42', borderRadius: '1px' }} />
      ))}
    </Box>
    <Typography variant="body2" component="span">
      {i18next.t('offer-individual-seating')}
    </Typography>
    <Tooltip
      title={
        <Box sx={{ maxWidth: 360, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
          {text}
        </Box>
      }
    >
      <IconButton size="small" aria-label={String(i18next.t('offer-individual-seating'))}>
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const EditorAccordionLineItem = memo(function EditorAccordionLineItem({
  contentBlock,
  contentBlockIndex,
  lineItemIndex,
  fullWidth,
  control,
  disabled,
  canChangeCounts,
  canChangeItems,
  canAddItems,
  canChangePrices,
  canDeleteItems,
  isPricesNet,
  canChangeLineItemCount,
  canChangeLineItemPrice,
  canChangeLineItemHeader,
  canDeleteLineItem,
  onLineItemRemoved,
  onLineItemChanged,
  onLineItemCopyPrices,
  onLineItemPriceComponentChanged,
  onLineItemPriceItemManuallyChanged,
  roomRequests,
}: IEditorAccordionLineItemProps) {
  const { watch, getValues, formState } = useFormContext<OfferVersionFormType>();
  const quickPriceCalculator = useContext(QuickPriceCalculatorContext);

  const { fields: componentsFields } = useFieldArray({
    control,
    name: `contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.components`,
  });

  const priceItemWatch = watch(
    `contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.priceItem`,
    _.get(contentBlock, `lineItems[${lineItemIndex}].priceItem`),
  );
  const countWatch = watch(
    `contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.count`,
    _.get(contentBlock, `lineItems[${lineItemIndex}].count`),
  );

  const componentsWatch = watch(`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.components`);
  const componentsLength = useMemo(() => (componentsWatch || []).length, [componentsWatch]);
  const firstComponent = useMemo(() => componentsWatch?.[0], [componentsWatch]);
  const skuWatch = watch(`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.sku`);
  const detailsWatch = watch(`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.details`) || '';
  const individualSeatingText = splitIndividualSeatingDetails(detailsWatch).seatingText;
  const isMultiComponentFreeText = useMemo(() => !skuWatch && componentsLength > 1, [skuWatch, componentsLength]);
  const lineItemId = watch(`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.id`);

  const canChangeHeader = useMemo(() => {
    return canChangeLineItemHeader ? canChangeLineItemHeader(contentBlockIndex, lineItemIndex) : false;
  }, [canChangeLineItemHeader, contentBlockIndex, lineItemIndex]);

  const canChangeCount = useMemo(() => {
    return canChangeLineItemCount ? canChangeLineItemCount(contentBlockIndex, lineItemIndex) : true;
  }, [canChangeLineItemCount, contentBlockIndex, lineItemIndex]);

  const canChangePrice = useMemo(() => {
    return canChangeLineItemPrice ? canChangeLineItemPrice(contentBlockIndex, lineItemIndex) : true;
  }, [canChangeLineItemPrice, contentBlockIndex, lineItemIndex]);

  const canDelete = useMemo(() => {
    return canDeleteLineItem ? canDeleteLineItem(contentBlockIndex, lineItemIndex) : true;
  }, [canDeleteLineItem, contentBlockIndex, lineItemIndex]);

  const handleCopyPrices = useCallback(() => {
    onLineItemCopyPrices(contentBlockIndex, lineItemIndex);
  }, [onLineItemCopyPrices, contentBlockIndex, lineItemIndex]);

  const handleRemove = useCallback(() => {
    onLineItemRemoved(contentBlockIndex, lineItemIndex);
  }, [onLineItemRemoved, contentBlockIndex, lineItemIndex]);

  const debouncedOnLineItemChanged = useCallback(
    _.debounce(() => {
      onLineItemChanged(contentBlockIndex, lineItemIndex);
    }, DEBOUNCE_DELAY_MS),
    [contentBlockIndex, lineItemIndex, onLineItemChanged],
  );

  const shouldRecalculateLineItem = !disabled
    && (canChangeItems || canChangePrices || canChangeCounts)
    && (canChangePrice || canChangeCount);

  // Count changes always recalculate line item totals
  useEffect(() => {
    if (!shouldRecalculateLineItem) return;

    debouncedOnLineItemChanged();

    return () => {
      debouncedOnLineItemChanged.cancel();
    };
  }, [countWatch, shouldRecalculateLineItem, debouncedOnLineItemChanged]);

  // Gross price edits for catalog / single-component lines
  useEffect(() => {
    if (!shouldRecalculateLineItem || isMultiComponentFreeText) return;

    debouncedOnLineItemChanged();

    return () => {
      debouncedOnLineItemChanged.cancel();
    };
  }, [priceItemWatch, isMultiComponentFreeText, shouldRecalculateLineItem, debouncedOnLineItemChanged]);

  // Nested component.price edits often keep the same array reference;
  // a price signature makes Anteil changes visible to useEffect.
  const componentsPricesKey = (componentsWatch ?? [])
    .map(c => `${c.taxTypeId}:${c.price}`)
    .join('|');

  // Anteil edits for special-request multi-tax lines; gross edits go through handlePriceItemBlur
  useEffect(() => {
    if (!shouldRecalculateLineItem || !isMultiComponentFreeText) return;

    debouncedOnLineItemChanged();

    return () => {
      debouncedOnLineItemChanged.cancel();
    };
  }, [componentsPricesKey, isMultiComponentFreeText, shouldRecalculateLineItem, debouncedOnLineItemChanged]);

  const handlePriceItemBlur = useCallback(() => {
    if (disabled || !canChangePrice || componentsLength <= 1) return;

    const currentPriceItem = getValues(`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.priceItem`);
    const currentComponents = getValues(`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.components`);
    const expectedSum = currentComponents.reduce((s, c) => s + (c.price === NO_PRICE ? 0 : c.price), 0);

    // Only redistribute if user changed the value
    if (Math.abs(currentPriceItem - expectedSum) > 0.01 && onLineItemPriceItemManuallyChanged) {
      onLineItemPriceItemManuallyChanged(contentBlockIndex, lineItemIndex, currentPriceItem);
    }
  }, [disabled, canChangePrice, componentsLength, contentBlockIndex, lineItemIndex, getValues, onLineItemPriceItemManuallyChanged]);

  const localizedLineItemHeader = useCallback(
    (lineItem: LineItemFormType) => {
      if (!lineItem?.sku || !quickPriceCalculator) return lineItem?.header;

      const product = quickPriceCalculator.allProductsDb?.find(p => p.sku === lineItem.sku);
      if (product) {
        return quickPriceSkuDisplayName(
          quickPriceCalculator.productTextSelector,
          quickPriceCalculator.hotel?.id,
          product.sku,
          lineItem.header || product.name,
          product.lookupCode,
          'product',
        );
      }

      const bundle = quickPriceCalculator.allBundlesDb?.find(b => b.sku === lineItem.sku);
      if (bundle) {
        return quickPriceSkuDisplayName(
          quickPriceCalculator.productTextSelector,
          quickPriceCalculator.hotel?.id,
          bundle.sku,
          lineItem.header || bundle.name,
          bundle.lookupCode,
          'bundle',
        );
      }

      const facility = quickPriceCalculator.allFacilitiesDb?.find(f => f.sku === lineItem.sku);
      if (facility) {
        return quickPriceSkuDisplayName(
          quickPriceCalculator.productTextSelector,
          quickPriceCalculator.hotel?.id,
          facility.sku,
          lineItem.header || facility.name,
          facility.lookupCode,
          'facility',
          facility,
        );
      }

      return lineItem.header;
    },
    [quickPriceCalculator],
  );

  return (
    <Grid container spacing={1}>
      <Grid item sm={fullWidth ? 1 : 2}>
        <FormInputNumber
          name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.count`}
          label={i18next.t('offerblock-lineitem-count')}
          control={control}
          required
          disabled={disabled || !canChangeCounts || !canChangeCount}
        />
      </Grid>
      <Grid item sm={fullWidth ? 6 : 8}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
            {hintValidationAlert(_.get(formState.errors, `contentBlocks[${contentBlockIndex}].lineItems[${lineItemIndex}]`))}
            <Typography component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <FormLabel
                name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}`}
                control={control}
                render={(lineItem: LineItemFormType) => localizedLineItemHeader(lineItem)}
              />
              {individualSeatingText && <IndividualSeatingHint text={individualSeatingText} />}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              {!disabled && (canChangeItems || canChangeHeader) && (
                <Grid item xs={12}>
                  <FormInputText
                    name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.header`}
                    label={i18next.t('offerblock-lineitem-header')}
                    control={control}
                    required
                    disabled={disabled || (canChangeLineItemHeader ? !canChangeHeader : !canChangeItems)}
                  />
                  <FormLabel
                    name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.sku`}
                    control={control}
                    render={sku => <>{sku && <Link to={`/products/sku/${sku}`}>{sku}</Link>}</>}
                  />
                </Grid>
              )}
              {!facilityIsSeminarRoom(quickPriceCalculator, skuWatch) && (
                <Grid item xs={12}>
                  <FormInputText
                    name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.details`}
                    label={i18next.t('offerblock-lineitem-details')}
                    control={control}
                    textFieldProps={{
                      multiline: true,
                      rows: 3,
                      value: localizeStoredSeminarDetails(detailsWatch, i18next.language),
                    }}
                    disabled={disabled}
                  />
                </Grid>
              )}
              <SeminarRoomLine namePrefix={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}`} control={control} disabled={disabled} contentBlock={contentBlock} lineItemIndex={lineItemIndex} roomRequests={roomRequests} quickPriceCalculator={quickPriceCalculator} header={localizedLineItemHeader(contentBlock.lineItems?.[lineItemIndex] as LineItemFormType)} />
              {!fullWidth && (
                <Grid item xs={12}>
                  {componentsLength > 1 && (
                    <Grid container spacing={1}>
                      <Grid item sm={6}>
                        {componentsFields.map((c, ci) => (
                          <EditorAccordionLineItemComponent
                            key={ci}
                            contentBlock={contentBlock}
                            contentBlockIndex={contentBlockIndex}
                            lineItemIndex={lineItemIndex}
                            componentIndex={ci}
                            control={control}
                            disabled={disabled || !canChangePrices}
                            isPricesNet={isPricesNet}
                            canChangeLineItemPrice={canChangeLineItemPrice}
                            onLineItemChanged={onLineItemChanged}
                            onLineItemPriceComponentChanged={onLineItemPriceComponentChanged}
                          />
                        ))}
                      </Grid>
                      <Grid item sm={6}>
                        <FormInputCurrency
                          name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.priceItem`}
                          label={isPricesNet ? i18next.t('offerblock-lineitem-priceitem-net') : i18next.t('offerblock-lineitem-priceitem-gross')}
                          currency="EUR"
                          emptyValue={NO_PRICE}
                          control={control}
                          disabled={disabled || !canChangePrices || !canChangePrice}
                          textFieldProps={{
                            onBlur: handlePriceItemBlur,
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}
                  {componentsLength <= 1 && (
                    <FormInputCurrency
                      name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.priceItem`}
                      label={_taxRateNameLabelItem(
                        isPricesNet,
                        firstComponent,
                        quickPriceCalculator,
                      )}
                      currency="EUR"
                      emptyValue={NO_PRICE}
                      control={control}
                      disabled={disabled || !canChangePrices || !canChangePrice}
                      required
                    />
                  )}
                </Grid>
              )}
              {!fullWidth && (
                <Grid item xs={12}>
                  {canChangePrices && !disabled && canChangePrice && (
                    <Button onClick={handleCopyPrices} startIcon={<DifferenceIcon />}>
                      {i18next.t('offerblock-lineitem-copyprices')}
                    </Button>
                  )}
                  {(canDeleteItems || !lineItemId) && !disabled && canDelete && (
                    <Button onClick={handleRemove} startIcon={<DeleteIcon />}>
                      {i18next.t('offerblock-lineitem-delete')}
                    </Button>
                  )}
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item sm={fullWidth ? 4 : 2}>
        {fullWidth && (
          <Grid container spacing={1}>
            <Grid item sm={8}>
              {componentsLength > 1 && (
                <LabeledFrame label={`${i18next.t('offerblock-lineitem-priceitem-header')}`}>
                  <Grid container spacing={1}>
                    <Grid item sm={6}>
                      {componentsFields.map((c, ci) => (
                        <EditorAccordionLineItemComponent
                          key={ci}
                          contentBlock={contentBlock}
                          contentBlockIndex={contentBlockIndex}
                          lineItemIndex={lineItemIndex}
                          componentIndex={ci}
                          control={control}
                          disabled={disabled || !canChangePrices}
                          isPricesNet={isPricesNet}
                          canChangeLineItemPrice={canChangeLineItemPrice}
                          onLineItemChanged={onLineItemChanged}
                          onLineItemPriceComponentChanged={onLineItemPriceComponentChanged}
                        />
                      ))}
                    </Grid>
                    <Grid item sm={6}>
                      <FormInputCurrency
                        name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.priceItem`}
                        label={isPricesNet ? i18next.t('offerblock-lineitem-priceitem-net') : i18next.t('offerblock-lineitem-priceitem-gross')}
                        currency="EUR"
                        emptyValue={NO_PRICE}
                        control={control}
                        disabled={disabled || !canChangePrices || !canChangePrice}
                        textFieldProps={{
                          onBlur: handlePriceItemBlur,
                        }}
                      />
                    </Grid>
                  </Grid>
                </LabeledFrame>
              )}
              {componentsLength <= 1 && (
                <FormInputCurrency
                  name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.priceItem`}
                  label={_taxRateNameLabelItem(
                    isPricesNet,
                    firstComponent,
                    quickPriceCalculator,
                  )}
                  currency="EUR"
                  emptyValue={NO_PRICE}
                  control={control}
                  disabled={disabled || !canChangePrices || !canChangePrice}
                  required
                />
              )}
            </Grid>
            {isPricesNet && (
              <Grid item sm={4}>
                <FormInputCurrency
                  name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.priceNet`}
                  label={i18next.t('offerblock-lineitem-pricenet')}
                  currency="EUR"
                  emptyValue={NO_PRICE}
                  control={control}
                  disabled
                />
              </Grid>
            )}
            {!isPricesNet && (
              <Grid item sm={4}>
                <FormInputCurrency
                  name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.priceGross`}
                  label={i18next.t('offerblock-lineitem-pricegross')}
                  currency="EUR"
                  emptyValue={NO_PRICE}
                  control={control}
                  disabled
                />
              </Grid>
            )}
          </Grid>
        )}
        {!fullWidth && isPricesNet && (
          <FormInputCurrency
            name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.priceNet`}
            label={i18next.t('offerblock-lineitem-pricenet')}
            currency="EUR"
            emptyValue={NO_PRICE}
            control={control}
            disabled
          />
        )}
        {!fullWidth && !isPricesNet && (
          <FormInputCurrency
            name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.priceGross`}
            label={i18next.t('offerblock-lineitem-pricegross')}
            currency="EUR"
            emptyValue={NO_PRICE}
            control={control}
            disabled
          />
        )}
      </Grid>
      {fullWidth && (
        <Grid item sm={1}>
          {canChangePrices && !disabled && canChangePrice && (
            <IconButton onClick={handleCopyPrices}>
              <Tooltip title={i18next.t('offerblock-lineitem-copyprices')}>
                <DifferenceIcon />
              </Tooltip>
            </IconButton>
          )}
          {(canDeleteItems || !lineItemId) && !disabled && canDelete && (
            <IconButton onClick={handleRemove}>
              <Tooltip title={i18next.t('offerblock-lineitem-delete')}>
                <DeleteIcon />
              </Tooltip>
            </IconButton>
          )}
        </Grid>
      )}
    </Grid>
  );
});

const EditorAccordionFormField = memo(function EditorAccordionFormField({
  cssStyles,
  contentBlock,
  contentBlockIndex,
  formFieldIndex,
  control,
  disabled,
  spaceId,
}: IEditorAccordionFormFieldProps) {
  const { watch, setValue, getValues } = useFormContext<OfferVersionFormType>();
  const type = watch(
    `contentBlocks.${contentBlockIndex}.formFields.${formFieldIndex}.type`,
    _.get(contentBlock, `formFields[${formFieldIndex}].type`),
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {type === 'TEXTAREA_RICHTEXT' && (
          <FormInputRichText
            options={{
              content_style: cssStyles,
            }}
            placeholder={watch(`contentBlocks.${contentBlockIndex}.formFields.${formFieldIndex}.placeholder`) || ''}
            name={`contentBlocks.${contentBlockIndex}.formFields.${formFieldIndex}.value`}
            label={watch(`contentBlocks.${contentBlockIndex}.formFields.${formFieldIndex}.label`)}
            control={control}
            required={watch(`contentBlocks.${contentBlockIndex}.formFields.${formFieldIndex}.required`) || false}
            disabled={disabled}
            spaceId={spaceId}
          />
        )}
        {type === 'TEXTAREA_PLAINTEXT' && (
          <FormInputText
            name={`contentBlocks.${contentBlockIndex}.formFields.${formFieldIndex}.defaultValue`}
            label={i18next.t('offerblock-formfield-defaultvalue')}
            control={control}
            required
            disabled={disabled}
          />
        )}
      </Grid>
    </Grid>
  );
});

const EditorAccordionLineItemComponent = memo(function EditorAccordionLineItemComponent({
  contentBlock,
  contentBlockIndex,
  lineItemIndex,
  componentIndex,
  control,
  disabled,
  isPricesNet,
  canChangeLineItemPrice,
  onLineItemChanged,
  onLineItemPriceComponentChanged,
}: IEditorAccordionLineItemComponentProps) {
  const quickPriceCalculator = useContext(QuickPriceCalculatorContext);
  const { watch } = useFormContext<OfferVersionFormType>();
  const priceWatch = watch(
    `contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.components.${componentIndex}.price`,
    _.get(contentBlock, `lineItems[${lineItemIndex}].components[${componentIndex}].price`),
  );

  const componentWatch = watch(`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.components.${componentIndex}`);
  const componentName = useMemo(() => componentWatch?.name, [componentWatch]);

  const canChangePrice = useMemo(() => {
    return canChangeLineItemPrice ? canChangeLineItemPrice(contentBlockIndex, lineItemIndex) : true;
  }, [canChangeLineItemPrice, contentBlockIndex, lineItemIndex]);

  // Updates priceItem from Anteile. For multi-component special requests,
  // onLineItemPriceComponentChanged also derives priceNet/priceGross/taxes.
  const debouncedOnPriceComponentChanged = useCallback(
    _.debounce(() => {
      onLineItemPriceComponentChanged(contentBlockIndex, lineItemIndex, componentIndex);
    }, DEBOUNCE_DELAY_MS),
    [contentBlockIndex, lineItemIndex, componentIndex, onLineItemPriceComponentChanged,],
  );

  useEffect(() => {
    if (disabled) return;
    if (!canChangePrice) return;

    debouncedOnPriceComponentChanged();

    return () => {
      debouncedOnPriceComponentChanged.cancel();
    };
  }, [priceWatch, disabled, canChangePrice, debouncedOnPriceComponentChanged]);

  return (
    <FormInputCurrency
      key={componentName}
      name={`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.components.${componentIndex}.price`}
      label={_taxRateNameLabelComponent(
        isPricesNet,
        componentWatch,
        quickPriceCalculator,
      )}
      currency="EUR"
      emptyValue={NO_PRICE}
      control={control}
      required
      disabled={disabled || !canChangePrice}
      textFieldProps={{ onBlur: () => debouncedOnPriceComponentChanged.flush() }}
    />
  );
});

/**
 * Main accordion component for editing offer content blocks.
 * Supports drag-and-drop reordering, line items with price components, form fields, and HTML templates.
 * Handles different block types: LINE_ITEMS, CANCELLATION_ITEMS, CUSTOM_FORM, PLAIN_HTML, INPUT_DAYS.
 * Uses debounced callbacks to prevent excessive price recalculation.
 */
export function EditorAccordion({
  cssStyles,
  contentBlock,
  contentBlockIndex,
  fullWidth,
  control,
  disabled,
  canChangeCounts,
  canChangePrices,
  canDeleteItems,
  canChangeItems,
  canAddItems,
  canChangeStructure,
  showHtmlTab,
  isPricesNet,
  priceListId,
  guestCount,
  isPremium,
  publicCompanyId,
  canChangeLineItemCount,
  canChangeLineItemPrice,
  canChangeLineItemHeader,
  canDeleteLineItem,
  showLineItem,
  isLineItemHighlighted,
  onDelete,
  onLineItemFreeAdded,
  onLineItemSKUAdded,
  onLineItemChanged,
  onLineItemCopyPrices,
  onLineItemPriceComponentChanged,
  onLineItemPriceItemManuallyChanged,
  onTotalsChanged,
  hotelMessage,
  spaceId,
  roomRequests,
}: IEditorAccordionProps) {
  const [tabValue, setTabValue] = React.useState<string | null>('data');

  const quickPriceCalculator = useContext(QuickPriceCalculatorContext);
  const {
    watch,
    formState: { errors: validationErrors },
  } = useFormContext<OfferVersionFormType>();

  const startDateWatch = watch('startDate');
  const lineItemDate = useMemo(
    () => moment(startDateWatch).add(contentBlock.day ?? 0, 'days').toDate(),
    [startDateWatch, contentBlock.day],
  );

  // Hierarchy may expose ancestor + local twins (same lookupCode/SKU); keep one selectable row (SM-2779).
  const addonProducts = useMemo(() => {
    if (!quickPriceCalculator?.allProductsDb?.length) return [];
    if (typeof quickPriceCalculator.getUniqueAddonProducts === 'function') {
      return quickPriceCalculator.getUniqueAddonProducts({
        date: lineItemDate,
        guestCount: guestCount ?? 0,
        itemCount: 1,
        priceListId,
        isPremium,
        publicCompanyId,
      });
    }
    return _.uniqBy(quickPriceCalculator.allProductsDb, p => p.sku);
  }, [quickPriceCalculator, lineItemDate, guestCount, priceListId, isPremium, publicCompanyId]);

  const addonBundles = useMemo(() => {
    if (!quickPriceCalculator?.allBundlesDb?.length) return [];
    const bundles = quickPriceCalculator.allBundlesDb;
    if (typeof quickPriceCalculator.getUniqueAddonBySku === 'function') {
      return quickPriceCalculator.getUniqueAddonBySku(bundles);
    }
    return _.uniqBy(bundles, b => b.sku);
  }, [quickPriceCalculator]);

  const addonFacilities = useMemo(() => {
    if (!quickPriceCalculator?.allFacilitiesDb?.length) return [];
    const facilities = quickPriceCalculator.allFacilitiesDb;
    if (typeof quickPriceCalculator.getUniqueAddonBySku === 'function') {
      return quickPriceCalculator.getUniqueAddonBySku(facilities);
    }
    return _.uniqBy(facilities, f => f.sku);
  }, [quickPriceCalculator]);

  const {
    fields: lineItemsFields,
    append: lineItemsAppend,
    remove: lineItemsRemove,
    replace: lineItemsReplace,
  } = useFieldArray({
    control,
    name: `contentBlocks.${contentBlockIndex}.lineItems`,
  });

  const _reassignSequenceLineItems = useCallback((lineItems: LineItemFormType[]) => {
    lineItems.forEach((li, index) => {
      li.sequence = index + 1;
    });
    lineItemsReplace(lineItems);
  }, [lineItemsReplace]);

  const {
    fields: formFieldsFields,
    append: formFieldsAppend,
    remove: formFieldsRemove,
    replace: formFieldsReplace,
  } = useFieldArray({
    control,
    name: `contentBlocks.${contentBlockIndex}.formFields`,
  });

  const _reassignSequenceFormFields = useCallback((formFields: FormFieldsFormType[]) => {
    formFields.forEach((li, index) => {
      li.sequence = index + 1;
    });
    formFieldsReplace(formFields);
  }, [formFieldsReplace]);

  const contentBlocks = watch('contentBlocks');
  const canDeleteDayBlock = useMemo(() => {
    if (!canDeleteItems || !canChangeStructure || disabled) return false;
    if ((contentBlock as any).day === -1) return true;
    const relevantBlocks = contentBlocks.filter(c => (c.type === 'LINE_ITEMS' || c.type === 'CANCELLATION_ITEMS') && (c as any).day >= 0);
    if (relevantBlocks.length <= 1) return false;
    const lastRelevantIndex = contentBlocks
      .map(c => c.type === 'LINE_ITEMS' || c.type === 'CANCELLATION_ITEMS')
      .lastIndexOf(true);
    return contentBlockIndex === lastRelevantIndex;
  }, [canDeleteItems, canChangeStructure, disabled, contentBlock, contentBlocks, contentBlockIndex]);

  const handleDeleteDay = useCallback(() => {
    onDelete(contentBlockIndex);
  }, [onDelete, contentBlockIndex]);

  const handleDeleteBlock = useCallback(() => {
    onDelete(contentBlockIndex);
  }, [onDelete, contentBlockIndex]);

  const handleLineItemRemoved = useCallback((lineItemIndex: number) => {
    lineItemsRemove(lineItemIndex);
    onTotalsChanged();
  }, [lineItemsRemove, onTotalsChanged]);

  return (
    <div>
      <Accordion defaultExpanded={contentBlock.type === 'LINE_ITEMS'}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          {hintValidationAlert(_.get(validationErrors, `contentBlocks[${contentBlockIndex}]`))}
          <Typography variant="h6">
            <FormLabel
              name={`contentBlocks.${contentBlockIndex}`}
              control={control}
              render={(contentBlock: ContentBlockFormType) => (
                <>
                  {contentBlock.type === 'CUSTOM_FORM' && (
                    <>
                      <span>
                        {i18next.t('offerblock-customform-header')} #{contentBlock.sequence}
                      </span>
                      <br />
                      <span>
                        <em>{watch(`contentBlocks.${contentBlockIndex}.blockDescription`)}</em>
                      </span>
                    </>
                  )}
                  {contentBlock.type === 'PLAIN_HTML' && (
                    <>
                      <span>
                        {i18next.t('offerblock-plainhtml-header')} #{contentBlock.sequence}
                      </span>
                      <br />
                      <span>
                        <em>{watch(`contentBlocks.${contentBlockIndex}.blockDescription`)}</em>
                      </span>
                    </>
                  )}
                  {contentBlock.type === 'LINE_ITEMS' && (
                    <>
                      <span>
                        {localizedOfferDay(contentBlock.day!)} -{' '}
                        {formatDate(moment.utc(watch('startDate'), 'YYYY-MM-DD').add(contentBlock.day!, 'days').toDate())} ({lineItemsFields.length})
                      </span>
                      <br />
                      <span>
                        <em>{watch(`contentBlocks.${contentBlockIndex}.blockDescription`)}</em>
                      </span>
                    </>
                  )}
                  {contentBlock.type === 'CANCELLATION_ITEMS' && (
                    <>
                      <span>
                        {i18next.t('offerblock-cancellationitems-header-phase', {
                          rate: contentBlock.cancellationRule ? formatPercentage(contentBlock.cancellationRule.rate, 0) : '',
                          daysToEvent: contentBlock.cancellationRule?.daysToEvent,
                        })}{' '}
                        ({lineItemsFields.length})
                      </span>
                      <br />
                      <span>
                        <em>{watch(`contentBlocks.${contentBlockIndex}.blockDescription`)}</em>
                      </span>
                    </>
                  )}
                  {contentBlock.type === 'INPUT_DAYS' && (
                    <>
                      <span>
                        {i18next.t('offerblock-inputdays-header-day')} {localizedOfferDay(contentBlock.day!)}
                      </span>
                      <br />
                      <span>
                        <em>{watch(`contentBlocks.${contentBlockIndex}.blockDescription`)}</em>
                      </span>
                    </>
                  )}
                </>
              )}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {contentBlock.type === 'CUSTOM_FORM' && (
                <>
                  {showHtmlTab && (
                    <Tabs value={tabValue}>
                      <Tab value="data" label={i18next.t('offerblock-customform-data-tab')} onClick={() => setTabValue('data')} />
                      <Tab value="template" label={i18next.t('offerblock-customform-template-tab')} onClick={() => setTabValue('template')} />
                    </Tabs>
                  )}
                  {tabValue === 'template' && showHtmlTab && (
                    <Grid container>
                      <Grid item xs={12}>
                        <FormInputText
                          name={`contentBlocks.${contentBlockIndex}.template`}
                          label={i18next.t('offerblock-customform-html')}
                          required
                          textFieldProps={{
                            multiline: true,
                            rows: 10,
                          }}
                          control={control}
                        />
                      </Grid>
                    </Grid>
                  )}
                  {tabValue === 'data' && (
                    <Grid container>
                      <Grid item xs={12}>
                        <DragDropContext
                          onDragEnd={result => {
                            if (result.destination && contentBlock.lineItems) {
                              const formFields = watch(`contentBlocks.${contentBlockIndex}.formFields`);
                              if (!formFields) return;
                              const [reorderedItem] = formFields.splice(result.source.index, 1);
                              formFields.splice(result.destination.index, 0, reorderedItem);
                              _reassignSequenceFormFields(formFields);
                            }
                          }}
                        >
                          <Droppable droppableId={`characters_${contentBlockIndex}`}>
                            {(provided: any) => (
                              <Grid
                                container
                                spacing={3}
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className={`characters_${contentBlockIndex}`}
                              >
                                {formFieldsFields.map((li, formFieldIndex) => (
                                  <Draggable
                                    key={`${li.id}_s2`}
                                    draggableId={`${contentBlockIndex}_${formFieldIndex}_s2`}
                                    index={formFieldIndex}
                                    isDragDisabled={disabled}
                                  >
                                    {(provided: any) => (
                                      <Grid item xs={12} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                        <Accordion>
                                          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                                            <Typography>
                                              {watch(`contentBlocks.${contentBlockIndex}.formFields.${formFieldIndex}.label`)} -{' '}
                                              {watch(`contentBlocks.${contentBlockIndex}.formFields.${formFieldIndex}.name`)}
                                              <br />
                                              <span>
                                                <em>
                                                  {i18next.t(
                                                    `enums-EOfferVersionContentBlockFormFieldType-${watch(
                                                      `contentBlocks.${contentBlockIndex}.formFields.${formFieldIndex}.type`,
                                                    )}`,
                                                  )}
                                                </em>
                                              </span>
                                            </Typography>
                                          </AccordionSummary>
                                          <AccordionDetails>
                                            <EditorAccordionFormField
                                              cssStyles={cssStyles}
                                              contentBlock={contentBlock}
                                              contentBlockIndex={contentBlockIndex}
                                              formFieldIndex={formFieldIndex}
                                              control={control}
                                              disabled={disabled}
                                              spaceId={spaceId}
                                            />
                                          </AccordionDetails>
                                        </Accordion>
                                      </Grid>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </Grid>
                            )}
                          </Droppable>
                        </DragDropContext>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
              {contentBlock.type === 'PLAIN_HTML' && showHtmlTab && (
                <FormInputRichText
                  options={{
                    content_style: cssStyles,
                  }}
                  name={`contentBlocks.${contentBlockIndex}.template`}
                  label={i18next.t('offerblock-plainhtml-html')}
                  control={control}
                  required={true}
                  disabled={disabled}
                  spaceId={spaceId}
                />
              )}
              {contentBlock.type === 'INPUT_DAYS' && showHtmlTab && (
                <FormInputText
                  name={`contentBlocks.${contentBlockIndex}.template`}
                  label={i18next.t('offerblock-inputdays-html')}
                  required
                  textFieldProps={{
                    multiline: true,
                    rows: 10,
                  }}
                  control={control}
                  disabled={disabled}
                />
              )}
              {(contentBlock.type === 'LINE_ITEMS' || contentBlock.type === 'CANCELLATION_ITEMS') && (
                <>
                  {showHtmlTab && (
                    <Tabs value={tabValue}>
                      <Tab value="data" label={i18next.t('offerblock-lineitems-data-tab')} onClick={() => setTabValue('data')} />
                      <Tab value="template" label={i18next.t('offerblock-lineitems-template-tab')} onClick={() => setTabValue('template')} />
                    </Tabs>
                  )}
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      {tabValue === 'template' && showHtmlTab && (
                        <>
                          <FormInputText
                            name={`contentBlocks.${contentBlockIndex}.template`}
                            label={i18next.t('offerblock-lineitems-html')}
                            required
                            textFieldProps={{
                              multiline: true,
                              rows: 10,
                            }}
                            control={control}
                            disabled={disabled}
                          />
                        </>
                      )}
                      {tabValue === 'data' && (
                        <Grid container>
                          <Grid item xs={12}>
                            <DragDropContext
                              onDragEnd={result => {
                                if (result.destination && contentBlock.lineItems) {
                                  const lineItems = watch(`contentBlocks.${contentBlockIndex}.lineItems`);
                                  if (!lineItems) return;
                                  const [reorderedItem] = lineItems.splice(result.source.index, 1);
                                  lineItems.splice(result.destination.index, 0, reorderedItem);
                                  _reassignSequenceLineItems(lineItems);
                                }
                              }}
                            >
                              <Droppable droppableId={`characters_${contentBlockIndex}`}>
                                {(provided: any) => (
                                  <Grid
                                    spacing={1}
                                    container
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className={`characters_${contentBlockIndex}`}
                                  >
                                    {lineItemsFields.map((li, lineItemIndex) => (
                                      (!showLineItem || showLineItem(contentBlockIndex, lineItemIndex)) && <Draggable
                                        key={`${li.id}_s2`}
                                        draggableId={`${contentBlockIndex}_${lineItemIndex}_s2`}
                                        index={lineItemIndex}
                                        isDragDisabled={!canChangeStructure || disabled}
                                      >
                                        {(provided: any) => {
                                          const isRequested =
                                            isLineItemHighlighted?.(contentBlockIndex, lineItemIndex) ??
                                            watch(`contentBlocks.${contentBlockIndex}.lineItems.${lineItemIndex}.source`) ===
                                            EOfferVersionContentBlockLineItemSource.REQUESTED;
                                          return (
                                            <Grid
                                              item
                                              xs={12}
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              sx={{
                                                border: isRequested ? '2px solid #f59e0b' : '1px solid transparent',
                                                borderRadius: 1,
                                                backgroundColor: isRequested ? 'rgba(245, 158, 11, 0.08)' : 'transparent',
                                                boxShadow: isRequested ? '0 0 0 4px rgba(245, 158, 11, 0.15)' : 'none',
                                              }}
                                            >
                                              <EditorAccordionLineItem
                                                contentBlock={contentBlock}
                                                contentBlockIndex={contentBlockIndex}
                                                lineItemIndex={lineItemIndex}
                                                fullWidth={fullWidth}
                                                control={control}
                                                disabled={disabled}
                                                canChangeCounts={canChangeCounts}
                                                canChangePrices={canChangePrices}
                                                canDeleteItems={canDeleteItems}
                                                canAddItems={canAddItems}
                                                canChangeItems={canChangeItems}
                                                canChangeStructure={canChangeStructure}
                                                isPricesNet={isPricesNet}
                                                canChangeLineItemCount={canChangeLineItemCount}
                                                canChangeLineItemPrice={canChangeLineItemPrice}
                                                canChangeLineItemHeader={canChangeLineItemHeader}
                                                canDeleteLineItem={canDeleteLineItem}
                                                onLineItemRemoved={() => handleLineItemRemoved(lineItemIndex)}
                                                onLineItemChanged={onLineItemChanged}
                                                onLineItemCopyPrices={onLineItemCopyPrices}
                                                onLineItemPriceComponentChanged={onLineItemPriceComponentChanged}
                                                onLineItemPriceItemManuallyChanged={onLineItemPriceItemManuallyChanged}
                                                roomRequests={roomRequests}
                                              />
                                            </Grid>
                                          );
                                        }}
                                      </Draggable>
                                    ))}
                                    {provided.placeholder}
                                  </Grid>
                                )}
                              </Droppable>
                            </DragDropContext>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {canAddItems && !disabled && (
                        <>
                          <Select
                            value={0}
                            displayEmpty
                            onChange={event => {
                              if (!event.target.value) return;

                              const value = `${event.target.value}`;

                              if (value === 'OTHER') {
                                const lineItemDate = moment(watch('startDate')).add(contentBlock.day!, 'days').toDate();
                                // Get all tax types for this country with country-specific rates on the event date
                                const taxTypes = quickPriceCalculator?.taxRateSelector?.taxTypes?.filter(tt => {
                                  if (!quickPriceCalculator) return false;
                                  if (tt.name === 'NOTAX' || tt.name === '0%') return false;

                                  const rate = quickPriceCalculator.taxRateSelector?.getTaxRate({
                                    country: quickPriceCalculator.hotel!.businessCountry,
                                    date: lineItemDate,
                                    taxTypeId: tt.id,
                                  });

                                  // Only include tax types with country-specific rates (not default rates)
                                  return rate && rate.country === quickPriceCalculator.hotel!.businessCountry;
                                }) || [];

                                if (taxTypes.length > 0) {
                                  const li = onLineItemFreeAdded(contentBlockIndex, taxTypes.map(tt => tt.id));

                                  if (li) {
                                    li.header = i18next.t('offerblock-lineitem-other') || 'Sonderwunsch';
                                    lineItemsAppend(li);
                                    onTotalsChanged();
                                  }
                                }

                                return;
                              }
                              const taxTypeId = parseInt(value);

                              if (!isNaN(taxTypeId) && quickPriceCalculator?.taxRateSelector?.taxTypes.find(t => t.id === taxTypeId)) {
                                const li = onLineItemFreeAdded(contentBlockIndex, taxTypeId);
                                if (li) {
                                  lineItemsAppend(li);
                                  onTotalsChanged();
                                }
                              } else {
                                const li = onLineItemSKUAdded(contentBlockIndex, value);
                                if (li) {
                                  lineItemsAppend(li);
                                  onTotalsChanged();
                                }
                              }
                            }
                            }
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 400,
                                },
                              },
                            }}
                          >
                            <MenuItem value={0}>
                              <em>{i18next.t('offerblock-lineitem-addsku')}</em>
                            </MenuItem>
                            <MenuItem value="OTHER">
                              {i18next.t('offerblock-lineitem-other')}
                            </MenuItem>
                            <MenuItem value={''} disabled>
                              <em>---------------------------</em>
                            </MenuItem>
                            {addonProducts.length > 0 &&
                              _.sortBy(addonProducts, p =>
                                `${quickPriceSkuDisplayName(
                                  quickPriceCalculator?.productTextSelector,
                                  quickPriceCalculator?.hotel?.id,
                                  p.sku,
                                  p.name,
                                  p.lookupCode,
                                  'product',
                                )} ${p.sku}`.toLocaleLowerCase(),
                              ).map((p, index) => (
                                <MenuItem key={`product_${p.id}_${index}`} value={p.sku}>
                                  {p.sku}{' '}
                                  {quickPriceSkuDisplayName(
                                    quickPriceCalculator?.productTextSelector,
                                    quickPriceCalculator?.hotel?.id,
                                    p.sku,
                                    p.name,
                                    p.lookupCode,
                                    'product',
                                  )}
                                </MenuItem>
                              ))}
                            <MenuItem value={''} disabled>
                              <em>---------------------------</em>
                            </MenuItem>
                            {addonBundles.length > 0 &&
                              _.sortBy(addonBundles, b =>
                                `${quickPriceSkuDisplayName(
                                  quickPriceCalculator?.productTextSelector,
                                  quickPriceCalculator?.hotel?.id,
                                  b.sku,
                                  b.name,
                                  b.lookupCode,
                                  'bundle',
                                )} ${b.sku}`.toLocaleLowerCase(),
                              ).map((b, index) => (
                                <MenuItem key={`bundle_${b.id}_${index}`} value={b.sku}>
                                  {b.sku}{' '}
                                  {quickPriceSkuDisplayName(
                                    quickPriceCalculator?.productTextSelector,
                                    quickPriceCalculator?.hotel?.id,
                                    b.sku,
                                    b.name,
                                    b.lookupCode,
                                    'bundle',
                                  )}
                                </MenuItem>
                              ))}
                            <MenuItem value={''} disabled>
                              <em>---------------------------</em>
                            </MenuItem>
                            {addonFacilities.length > 0 &&
                              _.sortBy(addonFacilities, f =>
                                `${quickPriceSkuDisplayName(
                                  quickPriceCalculator?.productTextSelector,
                                  quickPriceCalculator?.hotel?.id,
                                  f.sku,
                                  f.name,
                                  f.lookupCode,
                                  'facility',
                                  f,
                                )} ${f.sku}`.toLocaleLowerCase(),
                              ).map((f, index) => (
                                <MenuItem key={`facility_${f.id}_${index}`} value={f.sku}>
                                  {f.sku}{' '}
                                  {quickPriceSkuDisplayName(
                                    quickPriceCalculator?.productTextSelector,
                                    quickPriceCalculator?.hotel?.id,
                                    f.sku,
                                    f.name,
                                    f.lookupCode,
                                    'facility',
                                    f,
                                  )}
                                </MenuItem>
                              ))}
                          </Select>

                          <Typography variant="caption" color="text.primary" display="block" mt={1} fontStyle="italic" fontSize="0.7rem">
                            {i18next.t('offerblock-special-requests-info')}{' '}
                            (
                            {hotelMessage ? (
                              <Tooltip title={hotelMessage} arrow placement="top">
                                <span style={{ textDecoration: 'underline', color: '#ac2a6e', cursor: 'help' }}>
                                  {i18next.t('offerblock-show-special-requests')}
                                </span>
                              </Tooltip>
                            ) : (
                              i18next.t('offerblock-no-special-requests')
                            )}
                            )
                          </Typography>
                        </>
                      )}
                      {canDeleteDayBlock && (
                        <Button onClick={handleDeleteDay} startIcon={<DeleteIcon />}>
                          {i18next.t('offerblock-delete-day')}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </>
              )}

              {canChangeStructure && !disabled && contentBlock.type === 'PLAIN_HTML' && (
                <Button onClick={handleDeleteBlock} startIcon={<DeleteIcon />}>
                  {i18next.t('offerblock-delete')}
                </Button>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  )
}
